import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Instagram } from "lucide-react";
import { DEFAULT_GROUPED_PUBLIC_HOURS_ROWS, getGroupedPublicHoursRows } from "@/lib/publicHours";

// TikTok icon component
const TikTokIcon = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

const footerLinks = [
  { name: "Home", path: "/" },
  { name: "Services", path: "/services" },
  { name: "Gallery", path: "/gallery" },
  { name: "About", path: "/about" },
  { name: "Policies", path: "/policies" },
  { name: "Contact", path: "/contact" },
];

export default function Footer() {
  const [hours, setHours] = useState(DEFAULT_GROUPED_PUBLIC_HOURS_ROWS);

  useEffect(() => {
    let active = true;

    getGroupedPublicHoursRows().then((rows) => {
      if (active) {
        setHours(rows);
      }
    });

    return () => {
      active = false;
    };
  }, []);

  return (
    <footer
      data-testid="footer"
      className="bg-[#111111] text-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand */}
          <div>
            <Link
              to="/"
              data-testid="footer-logo"
              className="inline-block mb-4"
            >
              <span className="logo-text text-xl text-white block">
                DADADELUXE
              </span>
            </Link>
            <p className="text-white/60 text-sm leading-relaxed mb-4">
              Luxury wig installs and sew-ins. Flawless styling for confident beauty.
            </p>
            <div className="flex gap-3">
              <a
                href="https://www.instagram.com/dadadeluxe_/?hl=en"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-instagram"
                className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
                aria-label="Instagram"
              >
                <Instagram size={16} />
              </a>
              <a
                href="https://www.tiktok.com/@dadadeluxe"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-tiktok"
                className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
                aria-label="TikTok"
              >
                <TikTokIcon size={16} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-xs uppercase tracking-widest text-white/50 mb-4">
              Quick Links
            </h4>
            <nav className="flex flex-col gap-2">
              {footerLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`footer-link-${link.name.toLowerCase()}`}
                  className="footer-link text-sm"
                >
                  {link.name}
                </Link>
              ))}
            </nav>
          </div>

          {/* Hours */}
          <div>
            <h4 className="text-xs uppercase tracking-widest text-white/50 mb-4">
              Hours
            </h4>
            <div className="space-y-2">
              {hours.map((item, index) => (
                <div key={index} className="flex justify-between text-sm">
                  <span className="text-white/60">{item.shortDay}</span>
                  <span className="text-white">{item.time}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-xs uppercase tracking-widest text-white/50 mb-4">
              Contact
            </h4>
            <div className="space-y-2 text-sm">
              <p>
                <span className="text-white/60">Email:</span>
                <br />
                <a
                  href="mailto:bookings@dadadeluxe.com"
                  data-testid="footer-email"
                  className="text-white hover:text-white/80 transition-colors"
                >
                  bookings@dadadeluxe.com
                </a>
              </p>
              <p>
                <span className="text-white/60">Phone:</span>
                <br />
                <a
                  href="tel:+12157609772"
                  data-testid="footer-phone"
                  className="text-white hover:text-white/80 transition-colors"
                >
                  (215) 760-9772
                </a>
              </p>
            </div>

            <Link
              to="/book"
              data-testid="footer-book-btn"
              className="inline-block mt-4 px-5 py-2 bg-white text-[#111111] text-sm rounded hover:bg-white/90 transition-colors"
            >
              Book Now
            </Link>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-10 pt-6 border-t border-white/10 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-white/40 text-xs">
            © {new Date().getFullYear()} DadaDeLuxe. All rights reserved.
          </p>
          <div className="flex gap-4">
            <Link
              to="/admin"
              className="text-white/40 text-xs hover:text-white/60 transition-colors"
            >
              Admin Sign In
            </Link>
            <Link
              to="/policies"
              className="text-white/40 text-xs hover:text-white/60 transition-colors"
            >
              Privacy Policy
            </Link>
            <Link
              to="/policies"
              className="text-white/40 text-xs hover:text-white/60 transition-colors"
            >
              Terms
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
